import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import hashSum from 'hash-sum'

import { TemplateCurrentItemMissingError } from 'apps/templates/errors'
import { installedTemplateAdapter } from 'apps/templates/models/Template/adapter'
import {
  AdaptedInstalledTemplate,
  InstalledTemplate,
} from 'shared/api/common/schemas/templates/installedTemplate'
import { ProtectedData } from 'shared/api/common/schemas/templates/protectedData'
import { templateApi } from 'shared/api/requests/template'
import { templatesApi } from 'shared/api/requests/templates'
import { createAsyncAction } from 'shared/lib/redux'
import errorTrackingService from 'utils/services/errorTrackingService'

const Namespace = 'templates'
const InstalledCurrentTemplates = `${Namespace}Current`

export const fetchInstalledTemplate = createAsyncAction(
  `${InstalledCurrentTemplates}/fetchInstalledTemplate`,
  async (templateId: number) => {
    const response = await templatesApi.fetchTemplate({
      query: { template_installation_id: templateId },
    })
    return response.data.item
  },
)

export const editInstalledTemplateProtection = createAsyncAction(
  `${InstalledCurrentTemplates}/editInstalledTemplateProtection`,
  async ({
    status,
    pageId,
    installationId,
  }: {
    status: boolean
    pageId: string
    installationId: number
  }) => {
    const response = await templateApi.setIsProtected({
      body: { is_protected: status, page_id: pageId, template_installation_id: installationId },
    })
    return response.data.data
  },
)

interface CurrentInstalledTemplates {
  item: AdaptedInstalledTemplate | null
  fetching: boolean
  hash: string | null
  hasChanges: boolean
  error: string | null
}

const CurrentInstalledTemplatesInitialState: CurrentInstalledTemplates = {
  item: null,
  fetching: false,
  hash: null,
  hasChanges: false,
  error: null,
}

const { reducer: CurrentInstalledTemplateReducer } = createSlice({
  name: InstalledCurrentTemplates,
  initialState: CurrentInstalledTemplatesInitialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchInstalledTemplate.pending.type, (state) => {
        state.fetching = true
        state.error = null
      })

      .addCase(fetchInstalledTemplate.rejected.type, (state) => {
        state.fetching = false
      })

      .addCase(
        fetchInstalledTemplate.fulfilled.type,
        (state, action: PayloadAction<InstalledTemplate>) => {
          const { payload: item } = action

          const result = installedTemplateAdapter(item)

          if (result !== null) {
            state.item = result
            state.hash = hashSum(result)
          }

          state.fetching = false
          state.hasChanges = false
        },
      )

      .addCase(
        editInstalledTemplateProtection.fulfilled.type,
        (state, action: PayloadAction<ProtectedData>) => {
          const { is_template_installation_protected } = action.payload

          if (state.item !== null) {
            state.item.template_installation_access_data.is_protected =
              is_template_installation_protected
          } else {
            errorTrackingService.logWarning(
              new TemplateCurrentItemMissingError('setIsProtected is requested'),
              {
                scope: 'root',
                section: null,
              },
            )
          }
        },
      )
  },
})

export { CurrentInstalledTemplateReducer }
